import { AdminPingWebhooks } from './pages/AdminPingWebhooks';
import { PingPayoutDebugBank } from './pages/PingPayoutDebugBank';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LocalBankIdDebugProvider } from './context/LocalBankIdDebugContext';
import EnvironmentIndicator from './components/EnvironmentIndicator';
import './App.css';
import { Landing, Login, Register, TermsOfService, InvitationDecision, VerifyEmail, Onboarding, Home, PersonHome, LawyerHome, LawyerSettings, InspectorHome, InspectorSettings, CompanySettings, CompanyThemeSettings, NotificationSettings, MockAdyenOnboarding, CompanyProfile, CustomerList, CustomerView, CustomerInvite, CustomerInvitationStatus, ProjectList, ProjectView, ProjectCreate, SigningHandoff, SigningReturn, AdminHome, AdminCompanyList, AdminCompanyCreate, AdminCompanyView, AdminCompanyInvitationList, AdminCustomerList, AdminLawyerList, AdminLawyerCreate, AdminLawyerView, AdminLawyerDetailView, AdminInspectorList, AdminInspectorCreate, AdminInspectorView, AdminInspectorDetailView, AdminProfile, ImpersonationActivate, AdminSystemInfo, AdminVersions, PersonInvite, PersonInvitationStatus, PersonProjectList, AdminDocumentLibrary, AdminAgreements, Notifications, Messages, AdminNotifications, AdminAdminList, AdminContractReview, AdminAuditTrail, AdminTermsAcceptanceReport, AdminLawyerRequests, AdminInspectorRequests, Marketplace, LawyerProjectList, LawyerProjectView, InspectorProjectList, InspectorProjectView, AdminContractConfig, AdminProjectConfig, AdminKpiDefaults, AdminConflictSettings, AdminBankSettings, AdminBankGuide, AdminDebugSettings, AdminTestUsers, AdminRefundPolicySettings, PaymentCheckout, AdminTransactionList, AdminAccounting, CompanyAccountingReport, AdminTransactionDetail, AdminAdyenLedger, AdminAdyenAccounts, AdminAdyenWebhookLogs, AdminAdyenPaymentMethods, LawyerMarketplacePreview, InspectorMarketplacePreview, MyCompanies, MyLawyers, MyInspectors, Contact, Changelog, AdminCompanyRegistrations, AdminCustomerCreate, AdminCustomerInvitationList, AdminCustomerRegistrations } from './pages';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { RefundPolicy } from './pages/RefundPolicy';
import { DeliveryInformation } from './pages/DeliveryInformation';
import { DebugBank } from './pages/DebugBank';
import { FileView } from './pages/FileView';
import { SessionMonitor } from './components/SessionMonitor';
import { AdminSessionSettings } from './pages/AdminSessionSettings';
import { authenticatedHomePath, requiresEmailVerification, requiresOnboarding } from './utils/onboarding';
import { isMockAdyenOnboardingPage } from './pages/MockAdyenOnboarding';

function ProtectedRoute({
  children,
  adminOnly = false,
  staffOnly = false,
  companyOnly = false,
  allowPendingAccountSetup = false,
  allowUnverifiedEmail = false
}: {
  children: React.ReactElement;
  adminOnly?: boolean;
  staffOnly?: boolean;
  companyOnly?: boolean;
  allowPendingAccountSetup?: boolean;
  /** The invitation decision confirms the invited email itself (869fmx898). */
  allowUnverifiedEmail?: boolean;
}) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (adminOnly && !user?.isAdmin) {
    return <Navigate to="/home" replace />;
  }

  if (staffOnly && !user?.isAdmin && !user?.isSupport) {
    return <Navigate to="/home" replace />;
  }

  if (
    !allowPendingAccountSetup &&
    !allowUnverifiedEmail &&
    user &&
    requiresEmailVerification(user) &&
    !requiresOnboarding(user) &&
    location.pathname !== '/verify-email'
  ) {
    return <Navigate to="/verify-email" replace />;
  }

  if (
    !allowPendingAccountSetup &&
    user &&
    requiresOnboarding(user) &&
    location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  if (companyOnly && location.pathname.startsWith('/projects') && user) {
    const projectPrefix = user.isAdmin || user.personaType === 'admin'
      ? '/admin/projects'
      : user.personaType === 'person'
        ? '/person/projects'
        : user.personaType === 'lawyer'
          ? '/lawyer/projects'
          : user.personaType === 'inspector'
            ? '/inspector/projects'
            : null;
    if (projectPrefix) {
      return (
        <Navigate
          to={`${projectPrefix}${location.pathname.slice('/projects'.length)}${location.search}${location.hash}`}
          replace
        />
      );
    }
  }

  if (companyOnly && user?.personaType === 'person') {
    return <Navigate to="/home" replace />;
  }

  return children;
}

function LegacyCompanyProjectRoute() {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/projects/${id ?? ''}${location.search}${location.hash}`} replace />;
}

function PublicRoute({ children }: { children: React.ReactElement }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  const isInvitationRegistration =
    location.pathname === '/register' &&
    new URLSearchParams(location.search).has('invitation');
  const invitationId = isInvitationRegistration
    ? new URLSearchParams(location.search).get('invitation')
    : null;

  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (isAuthenticated && isInvitationRegistration && invitationId) {
    return <Navigate to={`/invitations/${invitationId}/decision`} replace />;
  }

  if (isAuthenticated) {
    return <Navigate to={authenticatedHomePath(user)} replace />;
  }

  return children;
}

// Component that renders the correct home page based on persona type
function HomeRoute() {
  const { user } = useAuth();

  if (user?.personaType === 'person') {
    return <PersonHome />;
  }

  if (user?.personaType === 'lawyer') {
    return <LawyerHome />;
  }

  if (user?.personaType === 'inspector') {
    return <InspectorHome />;
  }

  return <Home />;
}

function CompanySettingsRoute() {
  const location = useLocation();
  return isMockAdyenOnboardingPage(location.search)
    ? <MockAdyenOnboarding />
    : <CompanySettings />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/anvandarvillkor" element={<TermsOfService />} />
      <Route path="/integritet" element={<PrivacyPolicy />} />
      <Route path="/integritetspolicy" element={<Navigate to="/integritet" replace />} />
      <Route path="/aterbetalning-och-avbokning" element={<RefundPolicy />} />
      <Route path="/aterbetalningspolicy" element={<Navigate to="/aterbetalning-och-avbokning" replace />} />
      <Route path="/avbokningspolicy" element={<Navigate to="/aterbetalning-och-avbokning" replace />} />
      <Route path="/leveransinformation" element={<DeliveryInformation />} />
      <Route path="/leveranser" element={<Navigate to="/leveransinformation" replace />} />
      <Route path="/kontakt" element={<Contact />} />
      <Route path="/changelog" element={<Changelog />} />
      {/* MVP-städning: /services, /how, /demo borttagna som routes.
          Direktnavigering fångas av catch-all nedan och redirectas till "/". */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/person/companies" element={<Navigate to="/my-companies" replace />} />
      <Route path="/invite/status" element={<Navigate to="/invite-status" replace />} />
      <Route path="/company/projects/:id" element={<LegacyCompanyProjectRoute />} />
      <Route
        path="/invitations/:id/decision"
        element={
          <ProtectedRoute allowUnverifiedEmail>
            <InvitationDecision />
          </ProtectedRoute>
        }
      />
      {/* Protected routes */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomeRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <CompanyProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/company"
        element={
          <ProtectedRoute companyOnly>
            <CompanySettingsRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/theme"
        element={
          <ProtectedRoute companyOnly>
            <CompanyThemeSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/notifications"
        element={
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/files/:documentId"
        element={
          <ProtectedRoute>
            <FileView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/lawyer/marketplace-preview"
        element={
          <ProtectedRoute>
            <LawyerMarketplacePreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/lawyer"
        element={
          <ProtectedRoute>
            <LawyerSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lawyer/projects"
        element={
          <ProtectedRoute>
            <LawyerProjectList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lawyer/projects/:id"
        element={
          <ProtectedRoute>
            <LawyerProjectView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inspector/projects"
        element={
          <ProtectedRoute>
            <InspectorProjectList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inspector/projects/:id"
        element={
          <ProtectedRoute>
            <InspectorProjectView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/inspector/marketplace-preview"
        element={
          <ProtectedRoute>
            <InspectorMarketplacePreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/inspector"
        element={
          <ProtectedRoute>
            <InspectorSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute companyOnly>
            <CustomerList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/:id"
        element={
          <ProtectedRoute>
            <CustomerView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/invite"
        element={
          <ProtectedRoute companyOnly>
            <CustomerInvite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers/invite-status"
        element={
          <ProtectedRoute companyOnly>
            <CustomerInvitationStatus />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute companyOnly>
            <ProjectList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/create"
        element={
          <ProtectedRoute companyOnly>
            <ProjectCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute companyOnly>
            <ProjectView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts/:id/signing"
        element={
          <ProtectedRoute>
            <SigningHandoff />
          </ProtectedRoute>
        }
      />
      <Route
        path="/signing/return"
        element={
          <ProtectedRoute>
            <SigningReturn />
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/completed"
        element={
          <ProtectedRoute companyOnly>
            <ProjectList key="completed" completed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/accounting/company-report"
        element={
          <ProtectedRoute companyOnly>
            <CompanyAccountingReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/person/projects/completed"
        element={
          <ProtectedRoute>
            <PersonProjectList key="completed" completed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lawyer/projects/completed"
        element={
          <ProtectedRoute>
            <LawyerProjectList key="completed" completed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inspector/projects/completed"
        element={
          <ProtectedRoute>
            <InspectorProjectList key="completed" completed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/person/projects"
        element={
          <ProtectedRoute>
            <PersonProjectList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/person/projects/:id"
        element={
          <ProtectedRoute>
            <ProjectView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite"
        element={
          <ProtectedRoute>
            <PersonInvite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invite-status"
        element={
          <ProtectedRoute>
            <PersonInvitationStatus />
          </ProtectedRoute>
        }
      />
      {/* Unified profile: persons edit details + change password on /profile.
          Keep the old path working (bookmarks, the previous menu link). */}
      <Route path="/settings/profile" element={<Navigate to="/profile" replace />} />
      <Route
        path="/my-companies"
        element={
          <ProtectedRoute>
            <MyCompanies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-lawyers"
        element={
          <ProtectedRoute>
            <MyLawyers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-inspectors"
        element={
          <ProtectedRoute>
            <MyInspectors />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <Marketplace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-checkout"
        element={
          <ProtectedRoute>
            <PaymentCheckout />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route path="/debug-bank/payout/:payoutId" element={<ProtectedRoute><PingPayoutDebugBank /></ProtectedRoute>} />
      <Route path="/debug-bank/:paymentId" element={<ProtectedRoute><DebugBank /></ProtectedRoute>} />
      <Route
        path="/impersonation/activate"
        element={
          <ProtectedRoute allowPendingAccountSetup>
            <ImpersonationActivate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={<Navigate to="/admin/home" replace />}
      />
      <Route
        path="/admin/home"
        element={
          <ProtectedRoute adminOnly>
            <AdminHome />
          </ProtectedRoute>
        }
      />
      {/* The old approve/reject page is gone; contractors are managed in the list. */}
      <Route path="/admin/companies" element={<Navigate to="/admin/company-list" replace />} />
      <Route
        path="/admin/projects"
        element={
          <ProtectedRoute adminOnly>
            <ProjectList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/completed"
        element={
          <ProtectedRoute adminOnly>
            <ProjectList key="completed" completed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/create"
        element={
          <ProtectedRoute adminOnly>
            <ProjectCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/projects/:id"
        element={
          <ProtectedRoute adminOnly>
            <ProjectView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/company-list"
        element={
          <ProtectedRoute adminOnly>
            <AdminCompanyList />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/company-list/create" element={<ProtectedRoute adminOnly><AdminCompanyCreate /></ProtectedRoute>} />
      <Route
        path="/admin/company-list/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminCompanyView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/company-registrations"
        element={
          <ProtectedRoute adminOnly>
            <AdminCompanyRegistrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/company-invitations"
        element={
          <ProtectedRoute adminOnly>
            <AdminCompanyInvitationList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers"
        element={
          <ProtectedRoute adminOnly>
            <AdminCustomerList />
          </ProtectedRoute>
        }
      />
      <Route path="/admin/customers/create" element={<ProtectedRoute adminOnly><AdminCustomerCreate /></ProtectedRoute>} />
      <Route
        path="/admin/customer-invitations"
        element={
          <ProtectedRoute adminOnly>
            <AdminCustomerInvitationList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customer-registrations"
        element={
          <ProtectedRoute adminOnly>
            <AdminCustomerRegistrations />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/:id"
        element={
          <ProtectedRoute adminOnly>
            <CustomerView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/lawyers"
        element={
          <ProtectedRoute adminOnly>
            <AdminLawyerList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/lawyers/create"
        element={
          <ProtectedRoute adminOnly>
            <AdminLawyerCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/lawyers/view/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminLawyerDetailView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/lawyers/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminLawyerView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inspectors"
        element={
          <ProtectedRoute adminOnly>
            <AdminInspectorList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inspectors/create"
        element={
          <ProtectedRoute adminOnly>
            <AdminInspectorCreate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inspectors/view/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminInspectorDetailView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/inspectors/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminInspectorView />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute staffOnly>
            <AdminProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/documents/library"
        element={
          <ProtectedRoute adminOnly>
            <AdminDocumentLibrary />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/documents/agreements"
        element={
          <ProtectedRoute adminOnly>
            <AdminAgreements />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/company-info"
        element={
          <ProtectedRoute adminOnly>
            <AdminSystemInfo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/versions"
        element={
          <ProtectedRoute adminOnly>
            <AdminVersions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute adminOnly>
            <AdminNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/admin-list"
        element={
          <ProtectedRoute adminOnly>
            <AdminAdminList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports/contract-review"
        element={
          <ProtectedRoute adminOnly>
            <AdminContractReview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports/audit-trail"
        element={
          <ProtectedRoute adminOnly>
            <AdminAuditTrail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports/terms-acceptances"
        element={
          <ProtectedRoute adminOnly>
            <AdminTermsAcceptanceReport />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports/lawyer-requests"
        element={
          <ProtectedRoute adminOnly>
            <AdminLawyerRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports/inspector-requests"
        element={
          <ProtectedRoute adminOnly>
            <AdminInspectorRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/contract-config"
        element={
          <ProtectedRoute adminOnly>
            <AdminContractConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/project-config"
        element={
          <ProtectedRoute adminOnly>
            <AdminProjectConfig />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/kpi-defaults"
        element={
          <ProtectedRoute adminOnly>
            <AdminKpiDefaults />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/testers"
        element={
          <ProtectedRoute adminOnly>
            <AdminTestUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/debug"
        element={
          <ProtectedRoute adminOnly>
            <AdminDebugSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/conflict-settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminConflictSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/banks"
        element={
          <ProtectedRoute adminOnly>
            <AdminBankSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/system/banks/:bankId/guide"
        element={
          <ProtectedRoute adminOnly>
            <AdminBankGuide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/debug"
        element={
          <ProtectedRoute adminOnly>
            <AdminDebugSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transactions"
        element={
          <ProtectedRoute adminOnly>
            <AdminTransactionList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transactions/refund-policy"
        element={
          <ProtectedRoute adminOnly>
            <AdminRefundPolicySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/transactions/:id"
        element={
          <ProtectedRoute adminOnly>
            <AdminTransactionDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/accounting"
        element={
          <ProtectedRoute adminOnly>
            <AdminAccounting />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/adyen-payment-methods"
        element={
          <ProtectedRoute adminOnly>
            <AdminAdyenPaymentMethods />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/adyen-ledger"
        element={
          <ProtectedRoute adminOnly>
            <AdminAdyenLedger />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/adyen-accounts"
        element={
          <ProtectedRoute adminOnly>
            <AdminAdyenAccounts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/adyen-webhook-logs"
        element={
          <ProtectedRoute adminOnly>
            <AdminAdyenWebhookLogs />
          </ProtectedRoute>
        }
      />

      <Route path="/admin/ping-webhooks" element={<ProtectedRoute adminOnly><AdminPingWebhooks /></ProtectedRoute>} />
      <Route path="/admin/system/session-settings" element={<ProtectedRoute adminOnly><AdminSessionSettings /></ProtectedRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** One development bar for every page, signed in or not; it holds the test mode. */
function AppEnvironmentBar() {
  const { user } = useAuth();
  return <EnvironmentIndicator user={user} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SessionMonitor />
        <LocalBankIdDebugProvider>
          <AppEnvironmentBar />
          <AppRoutes />
        </LocalBankIdDebugProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
